import React, { createContext, useContext, useState, useEffect } from 'react';
import i18n from '../i18n';
import enTranslations from '../locales/en.json';
import esTranslations from '../locales/es.json';
import frTranslations from '../locales/fr.json';
import zhTranslations from '../locales/zh.json';

const translations = {
  en: enTranslations,
  es: esTranslations,
  fr: frTranslations,
  zh: zhTranslations
};

export const LANGUAGES = [
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇺🇸' },
  { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'French', nativeName: 'Français', flag: '🇫🇷' },
  { code: 'zh', name: 'Chinese', nativeName: '中文', flag: '🇨🇳' }
];

export const LANGUAGE_TTS_CONFIG = {
  en: {
    defaultVoice: 'shimmer',
    voices: ['shimmer', 'nova', 'alloy', 'echo', 'onyx', 'fable']
  },
  es: {
    defaultVoice: 'nova',
    voices: ['nova', 'shimmer', 'alloy']
  },
  fr: {
    defaultVoice: 'nova',
    voices: ['nova', 'shimmer', 'alloy']
  },
  zh: {
    defaultVoice: 'nova',
    voices: ['nova', 'alloy', 'shimmer']
  }
};

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(() => {
    const saved = localStorage.getItem('zazu-language');
    if (saved && translations[saved]) return saved;
    const browserLang = navigator.language?.split('-')[0];
    if (browserLang && translations[browserLang]) return browserLang;
    return 'en';
  });

  const setLanguage = (lang) => {
    setLanguageState(lang);
    i18n.changeLanguage(lang);
  };

  useEffect(() => {
    localStorage.setItem('zazu-language', language);
    if (i18n.language !== language) i18n.changeLanguage(language);
  }, [language]);

  // Translation function with nested key support (e.g., "auth.signIn")
  const t = (key, replacements = {}) => {
    const keys = key.split('.');
    let value = translations[language];
    
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        // Fallback to English
        value = translations['en'];
        for (const k2 of keys) {
          if (value && typeof value === 'object' && k2 in value) {
            value = value[k2];
          } else {
            return key; // Return key if not found
          }
        }
        break;
      }
    }
    
    // Handle replacements like {{name}}
    if (typeof value === 'string' && Object.keys(replacements).length > 0) {
      Object.entries(replacements).forEach(([k, v]) => {
        value = value.replace(new RegExp(`{{${k}}}`, 'g'), v);
      });
    }
    
    return typeof value === 'string' ? value : key;
  };

  // Get TTS config for current language
  const getTTSConfig = () => LANGUAGE_TTS_CONFIG[language] || LANGUAGE_TTS_CONFIG['en'];

  // Get recommended TTS voice for current language
  const getDefaultTTSVoice = () => {
    const config = LANGUAGE_TTS_CONFIG[language];
    return config?.defaultVoice || 'shimmer';
  };

  // Check if a voice is recommended for current language
  const isVoiceRecommended = (voice) => {
    const config = LANGUAGE_TTS_CONFIG[language];
    return config?.voices?.includes(voice) || false;
  };

  const value = {
    language,
    setLanguage,
    t,
    translations: translations[language],
    availableLanguages: LANGUAGES,
    getTTSConfig,
    getDefaultTTSVoice,
    isVoiceRecommended,
    isSpanish: language === 'es',
    isEnglish: language === 'en',
    isFrench: language === 'fr',
    isChinese: language === 'zh'
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
